import React from 'react';
import { useNavigate } from 'react-router-dom';

const tiers = [
  {
    id: 'free',
    name: 'Gratis',
    monthlyPrice: 0,
    annualPrice: 0,
    currency: 'kr',
    period: '/mnd',
    desc: 'Kom i gang med grunnleggende funksjoner — helt gratis.',
    features: [
      'Opptil 10 bomberegninger/mnd',
      'Grunnleggende trafikkinfo',
      'E-poststøtte',
      '1 lagret rute',
      'Standard kartvisning',
    ],
    notIncluded: [
      'Sanntids trafikkoppdateringer',
      'EV/Hybrid rabattoptimalisering',
      'Månedlige kostnadsrapporter',
      'Automatisk ruteoptimalisering',
      'Prioritert kundeservice',
    ],
    cta: 'Start gratis',
    ctaPath: '/register',
    popular: false,
    accent: '#004F98',
  },
  {
    id: 'premium',
    name: 'Premium',
    monthlyPrice: 499,
    annualPrice: 399,
    currency: 'kr',
    period: '/mnd',
    desc: 'Komplett tilgang for privatpersoner og profesjonelle sjåfører.',
    features: [
      'Ubegrenset bomberegninger',
      'Sanntids trafikkoppdateringer',
      'EV/Hybrid rabattoptimalisering',
      'Månedlige kostnadsrapporter',
      'Automatisk ruteoptimalisering',
      'Prioritert kundeservice',
      'Alle lagrede ruter',
      'Avansert kartvisning',
      'Eksport til PDF/Excel',
      'Flerkjøretøy-støtte',
    ],
    notIncluded: [],
    cta: 'Velg Premium',
    ctaPath: '/register',
    popular: true,
    accent: '#FF5722',
  },
];

const PricingTiers = ({ isAnnual }) => {
  const navigate = useNavigate();

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 48px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '32px', maxWidth: '900px', margin: '0 auto' }}>
        {tiers?.map((tier, i) => {
          const displayPrice = isAnnual ? tier?.annualPrice : tier?.monthlyPrice;
          const annualTotal = tier?.annualPrice * 12;
          const monthlyTotal = tier?.monthlyPrice * 12;
          const savings = monthlyTotal - annualTotal;

          return (
            <div
              key={tier?.id}
              style={{
                position: 'relative',
                background: tier?.popular ? 'rgba(255,87,34,0.05)' : 'rgba(255,255,255,0.02)',
                border: `2px solid ${tier?.popular ? 'rgba(255,87,34,0.35)' : 'rgba(224,228,232,0.1)'}`,
                borderRadius: '2px',
                padding: '48px 40px',
                marginTop: tier?.popular ? '-20px' : '0',
                boxShadow: tier?.popular ? '0 8px 40px rgba(255,87,34,0.08)' : 'none',
              }}
            >
              {/* Corner accent */}
              <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: '32px', height: '32px', borderTop: `3px solid ${tier?.accent}`, borderRight: `3px solid ${tier?.accent}` }} />
              <div style={{ position: 'absolute', bottom: '-2px', left: '-2px', width: '20px', height: '20px', borderBottom: `2px solid ${tier?.accent}`, borderLeft: `2px solid ${tier?.accent}`, opacity: 0.4 }} />

              {/* Popular badge */}
              {tier?.popular && (
                <div style={{ position: 'absolute', top: '-16px', left: '50%', transform: 'translateX(-50%)', background: '#FF5722', border: '2px solid rgba(255,87,34,0.5)', borderRadius: '2px', padding: '5px 20px', whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFFFFF' }}>Mest populær</span>
                </div>
              )}

              {/* Tier name */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '32px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '-0.01em' }}>{tier?.name}</span>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--color-muted-foreground)', marginBottom: '32px', lineHeight: 1.6 }}>{tier?.desc}</p>

              {/* Price */}
              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '48px', fontWeight: 700, color: tier?.popular ? '#FF5722' : 'var(--color-foreground)', letterSpacing: '-0.03em', lineHeight: 1 }}>
                    {tier?.currency} {displayPrice?.toLocaleString('nb-NO')}
                  </span>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '15px', fontWeight: 500, color: 'var(--color-muted-foreground)' }}>{tier?.period}</span>
                </div>
                {/* Annual savings info */}
                {tier?.popular && isAnnual && savings > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)', textDecoration: 'line-through' }}>kr {tier?.monthlyPrice}/mnd</span>
                    <div style={{ background: 'rgba(255,87,34,0.12)', border: '1px solid rgba(255,87,34,0.3)', borderRadius: '2px', padding: '2px 8px' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, color: '#FF5722', letterSpacing: '0.06em' }}>SPAR kr {savings?.toLocaleString('nb-NO')}/år</span>
                    </div>
                  </div>
                )}
                {tier?.popular && !isAnnual && (
                  <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)', marginTop: '6px' }}>
                    Bytt til årlig og spar <span style={{ color: '#FF5722', fontWeight: 600 }}>kr {savings?.toLocaleString('nb-NO')}/år</span>
                  </p>
                )}
              </div>

              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', margin: '28px 0' }} />

              {/* Features included */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {tier?.features?.map(f => (
                  <li key={f} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tier?.accent} strokeWidth="2.5" style={{ flexShrink: 0, marginTop: '2px' }}><polyline points="20 6 9 17 4 12" /></svg>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-foreground)', lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
                {tier?.notIncluded?.map(f => (
                  <li key={f} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', opacity: 0.4 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E0E4E8" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: '2px' }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>

              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', margin: '28px 0' }} />

              {/* CTA */}
              <button
                onClick={() => navigate(tier?.ctaPath)}
                style={{
                  width: '100%',
                  background: tier?.popular ? '#FF5722' : 'transparent',
                  border: `2px solid ${tier?.accent}`,
                  borderRadius: '2px',
                  padding: '16px 24px',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '18px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: tier?.popular ? '#FFFFFF' : 'var(--color-foreground)',
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  if (!tier?.popular) {
                    e.currentTarget.style.background = 'rgba(0,79,152,0.1)';
                  } else {
                    e.currentTarget.style.background = '#E64A19';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = tier?.popular ? '#FF5722' : 'transparent';
                }}
              >
                {tier?.cta}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingTiers;
